import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowRight,
  Building2,
  Loader2,
  MoreVertical,
  Pause,
  Play,
  Plus,
  Search,
  Trash2,
} from "lucide-react";
import { api, apiError, formatDate } from "@/lib/api";
import { useConfirm } from "@/components/Confirm";
import { useTenant } from "@/context/TenantContext";
import { cacheGet, cacheKey, cacheSet } from "@/lib/cache";
import { useDelayedLoading } from "@/lib/useDelayedLoading";
import OwnerLayout from "@/components/OwnerLayout";
import { EmptyState, LoadingPanel } from "@/components/Bits";
import { BulkActionBar, SelectBox, runBulk, useSelection } from "@/components/BulkSelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

const BLANK = {
  name: "",
  contact_email: "",
  admin_name: "",
  admin_email: "",
  admin_password: "",
};

function NewClientDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setForm(BLANK);
  }, [open]);

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error("Give the client a name");
      return;
    }
    if (form.admin_email && form.admin_password.length < 8) {
      toast.error("The admin password needs at least 8 characters");
      return;
    }
    setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.admin_email) {
        delete payload.admin_email;
        delete payload.admin_password;
        delete payload.admin_name;
      }
      const { data } = await api.post("/tenants", payload);
      toast.success(`${data.tenant.name} added`);
      onOpenChange(false);
      onCreated(data.tenant);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[540px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Add a client</DialogTitle>
          <DialogDescription>
            They get their own conversations and team. Nothing is shared
            with your other client accounts.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div className="space-y-1.5">
            <Label htmlFor="cname">Business name</Label>
            <Input
              id="cname"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Their business name"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cemail">Contact email</Label>
            <Input
              id="cemail"
              type="email"
              value={form.contact_email}
              onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
              placeholder="Optional"
            />
          </div>

          <div className="rounded-xl border border-line p-4 space-y-3">
            <div>
              <div className="text-[13.5px] font-medium text-ink">Their admin login</div>
              <p className="text-[12.5px] text-muted2 mt-0.5">
                Optional — you can work in the client yourself and add this later.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="aname">Name</Label>
                <Input
                  id="aname"
                  value={form.admin_name}
                  onChange={(e) => setForm({ ...form, admin_name: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="aemail">Email</Label>
                <Input
                  id="aemail"
                  type="email"
                  value={form.admin_email}
                  onChange={(e) => setForm({ ...form, admin_email: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="apass">Temporary password</Label>
              <Input
                id="apass"
                value={form.admin_password}
                onChange={(e) => setForm({ ...form, admin_password: e.target.value })}
                placeholder="At least 8 characters"
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy} className="bg-brand hover:bg-brand-hover">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add client"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteDialog({ tenant, onOpenChange, onDeleted }) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setTyped("");
  }, [tenant]);

  if (!tenant) return null;
  const matches = typed.trim() === tenant.name;

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/tenants/${tenant.tenant_id}`, { params: { confirm: typed.trim() } });
      toast.success(`${tenant.name} deleted`);
      onOpenChange(false);
      onDeleted();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!tenant} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Delete {tenant.name}?</DialogTitle>
          <DialogDescription>
            This removes all of their conversations and messages, and all{" "}
            {tenant.user_count} of their logins. It can't be undone.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5 py-1">
          <Label htmlFor="confirm">
            Type <span className="font-medium text-ink">{tenant.name}</span> to confirm
          </Label>
          <Input id="confirm" value={typed} onChange={(e) => setTyped(e.target.value)} />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={remove}
            disabled={!matches || busy}
            className="bg-stock-out hover:bg-rose-700 text-white"
          >
            Delete permanently
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Owner-console page, not scoped to any one client business — "owner" is a
// fixed cache scope rather than a tenantId.
const CK = cacheKey("accounts", "owner");

export default function Accounts() {
  const { setTenantId, refreshTenants } = useTenant();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const cached = cacheGet(CK);
  // Seeded from cache so revisiting (even after a reload) shows the list
  // instantly instead of flashing a loading skeleton — see lib/cache.js.
  const [rows, setRows] = useState(() => cached || []);
  const [loading, setLoading] = useState(() => !cached);
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [q, setQ] = useState("");
  // Only show a loading skeleton once a fetch has actually taken a moment —
  // a cached/fast load resolves before the delay and never flashes one.
  const showLoading = useDelayedLoading(loading);

  const load = useCallback(async () => {
    if (!cacheGet(CK)) setLoading(true);
    try {
      const { data } = await api.get("/tenants");
      setRows(data);
      cacheSet(CK, data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const enter = async (tenant) => {
    setTenantId(tenant.tenant_id);
    await refreshTenants();
    navigate("/dashboard");
  };

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((t) => (t.name || "").toLowerCase().includes(term));
  }, [rows, q]);

  const toggleActive = async (tenant) => {
    try {
      await api.put(`/tenants/${tenant.tenant_id}`, { active: !tenant.active });
      toast.success(tenant.active ? `${tenant.name} suspended` : `${tenant.name} reactivated`);
      load();
      refreshTenants();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  // Bulk select — the checkbox on each account card. selection itself can
  // hold ids that have since scrolled out of view (a search change, say);
  // everything below only cares about the ones still visible/filtered.
  const selection = useSelection();
  const selectedRows = useMemo(
    () => filtered.filter((t) => selection.isSelected(t.tenant_id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filtered, selection.selected]
  );

  const bulkSuspend = async () => {
    const eligible = selectedRows.filter((t) => t.active);
    const skipped = selectedRows.length - eligible.length;
    if (!eligible.length) {
      toast.error("None of the selected accounts are active");
      return;
    }
    const { ok, failed } = await runBulk(eligible, (t) => api.put(`/tenants/${t.tenant_id}`, { active: false }));
    if (ok) toast.success(`${ok} account${ok === 1 ? "" : "s"} suspended${skipped ? ` · ${skipped} skipped (already suspended)` : ""}`);
    if (failed) toast.error(`${failed} couldn't be suspended`);
    selection.clear();
    load();
    refreshTenants();
  };

  const bulkReactivate = async () => {
    const eligible = selectedRows.filter((t) => !t.active);
    const skipped = selectedRows.length - eligible.length;
    if (!eligible.length) {
      toast.error("None of the selected accounts are suspended");
      return;
    }
    const { ok, failed } = await runBulk(eligible, (t) => api.put(`/tenants/${t.tenant_id}`, { active: true }));
    if (ok) toast.success(`${ok} account${ok === 1 ? "" : "s"} reactivated${skipped ? ` · ${skipped} skipped (already active)` : ""}`);
    if (failed) toast.error(`${failed} couldn't be reactivated`);
    selection.clear();
    load();
    refreshTenants();
  };

  const bulkDelete = async () => {
    if (!selectedRows.length) return;
    const ok2 = await confirm({
      title: `Delete ${selectedRows.length} client account${selectedRows.length === 1 ? "" : "s"}?`,
      body: "This removes every one of their conversations, messages and logins for good. It can't be undone.",
      confirmText: "Delete",
      destructive: true,
    });
    if (!ok2) return;
    const { ok, failed } = await runBulk(selectedRows, (t) =>
      api.delete(`/tenants/${t.tenant_id}`, { params: { confirm: t.name } })
    );
    if (ok) toast.success(`${ok} account${ok === 1 ? "" : "s"} deleted`);
    if (failed) toast.error(`${failed} couldn't be deleted`);
    selection.clear();
    load();
    refreshTenants();
  };

  return (
    <OwnerLayout
      title="Accounts"
      subtitle={
        rows.length
          ? `${rows.length} client${rows.length === 1 ? "" : "s"}`
          : "Add your first client to get started"
      }
      action={
        <Button onClick={() => setAdding(true)} className="bg-brand hover:bg-brand-hover">
          <Plus className="w-4 h-4 sm:mr-2" />
          <span className="hidden sm:inline">Add client</span>
        </Button>
      }
    >
      <>
        {selectedRows.length > 0 ? (
          <BulkActionBar
            count={selectedRows.length}
            noun={selectedRows.length === 1 ? "account selected" : "accounts selected"}
            onClear={selection.clear}
            actions={[
              { key: "suspend", label: "Suspend", icon: Pause, onClick: bulkSuspend },
              { key: "reactivate", label: "Reactivate", icon: Play, onClick: bulkReactivate },
              { key: "delete", label: "Delete", icon: Trash2, tone: "danger", onClick: bulkDelete },
            ]}
          />
        ) : (
          rows.length > 0 && (
            <div className="mb-4 relative max-w-sm w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted2" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search accounts…" className="pl-9" />
            </div>
          )
        )}
        {loading ? (
          showLoading ? <LoadingPanel rows={4} /> : null
        ) : rows.length === 0 ? (
          <div className="panel">
            <EmptyState
              icon={Building2}
              title="No clients yet"
              body="Add your first client and they'll get their own conversations, team and settings. Their data stays completely separate from every other client."
              action={
                <Button onClick={() => setAdding(true)} className="bg-brand hover:bg-brand-hover">
                  <Plus className="w-4 h-4 mr-2" /> Add client
                </Button>
              }
            />
          </div>
        ) : filtered.length === 0 ? (
          <div className="panel">
            <EmptyState icon={Building2} title="No accounts match" body="Try a different search." />
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((t) => (
              <div
                key={t.tenant_id}
                className={cn(
                  "panel panel-pad flex flex-col relative",
                  !t.active && "opacity-60",
                  selection.isSelected(t.tenant_id) && "ring-2 ring-brand-ring border-brand"
                )}
              >
                <SelectBox
                  checked={selection.isSelected(t.tenant_id)}
                  onClick={() => selection.toggle(t.tenant_id)}
                  className="absolute top-3 left-3 z-10"
                />
                <div className="flex items-start justify-between gap-3 pl-6">
                  <div className="min-w-0">
                    <div className="font-display text-[17px] font-semibold text-ink leading-snug truncate">
                      {t.name}
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="p-1.5 rounded-lg text-muted2 hover:bg-brand-light shrink-0">
                        <MoreVertical className="w-4 h-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => toggleActive(t)}>
                        {t.active ? (
                          <>
                            <Pause className="w-3.5 h-3.5 mr-2" /> Suspend access
                          </>
                        ) : (
                          <>
                            <Play className="w-3.5 h-3.5 mr-2" /> Reactivate
                          </>
                        )}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => setDeleting(t)}
                        className="text-stock-out focus:text-stock-out"
                      >
                        <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete client
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {!t.active && (
                  <div className="mt-3 inline-flex self-start items-center gap-1.5 rounded-full bg-gold-light text-gold border border-amber-200 px-2.5 py-0.5 text-[11.5px]">
                    Suspended
                  </div>
                )}

                <div className="grid grid-cols-2 gap-2 mt-4 pt-4 border-t border-line/70">
                  {[
                    { label: "Team", value: t.user_count },
                    { label: "Conversations", value: t.conversation_count },
                  ].map((s) => (
                    <div key={s.label}>
                      <div className="font-mono text-[15px] text-ink tnum leading-none">
                        {s.value}
                      </div>
                      <div className="text-[10.5px] text-muted2 mt-1.5">{s.label}</div>
                    </div>
                  ))}
                </div>

                <div className="text-[11.5px] text-muted2 mt-3">
                  Added {formatDate(t.created_at)}
                </div>

                <Button
                  onClick={() => enter(t)}
                  variant="outline"
                  className="w-full mt-4 justify-between"
                >
                  Open workspace
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      <NewClientDialog
        open={adding}
        onOpenChange={setAdding}
        onCreated={async (tenant) => {
          await load();
          await refreshTenants();
          enter(tenant);
        }}
      />
      <DeleteDialog
        tenant={deleting}
        onOpenChange={() => setDeleting(null)}
        onDeleted={() => {
          load();
          refreshTenants();
        }}
      />
      </>
    </OwnerLayout>
  );
}
